import { formatMoney, minorUnits, type Cents } from '@keel/core';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useAppStore } from './store';

export interface FormatContextValue {
  /** ISO-4217 currency used for figures on screen (current entity's base currency). */
  readonly currency: string;
  /** BCP-47 locale for numbers and dates. */
  readonly locale: string;
}
const FormatContext = createContext<FormatContextValue>({ currency: 'AUD', locale: 'en-AU' });

/** Set by the shell from the current entity; components read it via `useMoney()` / `useDates()`. */
export const FormatProvider = ({ currency, locale, children }: FormatContextValue & { children: ReactNode }) => {
  const value = useMemo(() => ({ currency, locale }), [currency, locale]);
  return <FormatContext.Provider value={value}>{children}</FormatContext.Provider>;
};
export const useFormatContext = (): FormatContextValue => useContext(FormatContext);

export interface MoneyFormatOptions {
  readonly currency?: string;
  readonly sign?: 'auto' | 'always' | 'never';
  readonly compact?: boolean;
  readonly hideSymbol?: boolean;
}

/** Narrow currency symbol for a currency in a locale, e.g. "$", "£". */
export const currencySymbol = (currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
    .formatToParts(0)
    .find((p) => p.type === 'currency')?.value ?? currency;

/**
 * Money formatting bound to the current entity's currency & locale, plus the privacy-blur flag.
 * `format(12345)` → "$123.45". Use `<MoneyText>` for display; this hook is for strings (aria labels, toasts).
 */
export const useMoney = () => {
  const { currency, locale } = useFormatContext();
  const blur = useAppStore((s) => s.privacyBlur);
  const format = useCallback(
    // `|| 0` turns -0 (e.g. a negated zero spend) into 0 so it never shows as "-$0.00"
    (cents: Cents, opts: MoneyFormatOptions = {}): string => formatMoney(cents || 0, opts.currency ?? currency, locale, opts),
    [currency, locale],
  );
  const symbol = useMemo(() => currencySymbol(currency, locale), [currency, locale]);
  return { format, currency, locale, blur, symbol, minorUnits: minorUnits(currency) };
};
